export const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] };

export function parseKm(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  let text = String(value).trim().replace(/\s/g, '');
  if (text.includes(',') && text.includes('.')) {
    text = text.replace(/\./g, '').replace(',', '.');
  } else if (text.includes(',')) {
    text = text.replace(',', '.');
  }
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
}

export function formatKm(value) {
  const number = parseKm(value);
  if (number == null) return '—';
  return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
}

export function hasKmValue(value) {
  return value != null && String(value).trim() !== '';
}

function normalizeHex(value) {
  return String(value || '').trim().toLowerCase();
}

export function inferTipo(properties = {}) {
  if (properties.tipo) return properties.tipo;
  const cor = normalizeHex(properties.cor || properties.corDetectada || properties.color);
  if (cor === '#aa00ff') return 'PROTECAO';
  if (cor === '#0000ff') return 'NA';
  return 'POSTE';
}

export function normalizeServicos(collection) {
  if (!collection || collection.type !== 'FeatureCollection' || !Array.isArray(collection.features)) {
    return EMPTY_COLLECTION;
  }
  return {
    type: 'FeatureCollection',
    features: collection.features
      .filter((feature) => feature && feature.geometry && feature.geometry.type === 'Point')
      .map((feature) => {
        const properties = feature.properties || {};
        return {
          ...feature,
          properties: {
            ...properties,
            tipo: 'SERVICO',
            nome: properties.codigo || properties.nome || '',
          },
        };
      }),
  };
}

export function normalizeCollection(collection) {
  if (!collection || collection.type !== 'FeatureCollection' || !Array.isArray(collection.features)) {
    return EMPTY_COLLECTION;
  }
  return {
    type: 'FeatureCollection',
    features: collection.features.map((feature, index) => {
      const properties = feature.properties || {};
      return {
        ...feature,
        id: index,
        properties: {
          ...properties,
          nome: properties.nome || properties.name || String(feature.id != null ? feature.id : index),
          tipo: inferTipo(properties),
        },
      };
    }),
  };
}

export function pointKey(point) {
  return `${point.nome}::${point.tipo || ''}`;
}

export function toPontoFinal(point) {
  return { nome: point.nome, tipo: point.tipo || 'POSTE' };
}

export function pontoParaLista(point) {
  const item = toPontoFinal(point);
  if (point && point.nomeReal) item.nomeReal = point.nomeReal;
  return item;
}

export function rotuloDoPonto(point) {
  if (!point) return '';
  const nome = point.nome || '';
  const nomeReal = point.nomeReal && String(point.nomeReal).trim();
  if (nomeReal && nomeReal !== nome) return `${nome} — ${nomeReal}`;
  return nome;
}

const ATRIBUTOS_JA_NA_FICHA = new Set([
  'Posição operada',
  'Posição planejada',
  'Fase',
  'Estado',
  'Tensão nominal',
  'Corrente nominal',
  'kVA Distribuído',
  'kVA Primário',
  'kVA Rural',
  'kVA total',
  'Clientes Distribuídos',
  'Clientes Primários',
  'Clientes Rurais',
  'Clientes Totais',
]);

function asObject(value) {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (err) {
      return {};
    }
  }
  if (typeof value !== 'object' || Array.isArray(value)) return {};
  return value;
}

function asNumber(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function featureDoPonto(rede, point) {
  if (!point) return null;
  const key = pointKey(point);
  const matches = (rede.features || []).filter((feature) => pointKey(feature.properties || {}) === key);
  if (!matches.length) return null;
  return matches.find((feature) => feature.geometry && feature.geometry.type === 'Point') || matches[0];
}

function coordenadasDaLinha(geometry) {
  if (!geometry) return [];
  if (geometry.type === 'LineString') return geometry.coordinates || [];
  if (geometry.type === 'MultiLineString') return (geometry.coordinates || []).reduce((lista, parte) => lista.concat(parte), []);
  return [];
}

const METROS_NO_TRECHO = 4;

function segmentosDoCaminho(caminho) {
  const segmentos = [];
  ((caminho && caminho.features) || []).forEach((feature) => {
    const coordenadas = coordenadasDaLinha(feature.geometry);
    for (let i = 1; i < coordenadas.length; i += 1) {
      segmentos.push([coordenadas[i - 1], coordenadas[i]]);
    }
  });
  return segmentos;
}

function metrosAteSegmento(ponto, inicio, fim) {
  if (!ponto || ponto.length < 2 || !inicio || !fim) return Infinity;
  const dx = fim[0] - inicio[0];
  const dy = fim[1] - inicio[1];
  const comprimento = (dx * dx) + (dy * dy);
  let t = comprimento ? (((ponto[0] - inicio[0]) * dx) + ((ponto[1] - inicio[1]) * dy)) / comprimento : 0;
  t = Math.max(0, Math.min(1, t));
  const x = inicio[0] + (t * dx);
  const y = inicio[1] + (t * dy);
  const metrosX = (ponto[0] - x) * 111320 * Math.cos((ponto[1] * Math.PI) / 180);
  const metrosY = (ponto[1] - y) * 110540;
  return Math.hypot(metrosX, metrosY);
}

function metrosEntre(a, b) {
  if (!a || !b || a.length < 2 || b.length < 2) return Infinity;
  const lat = ((Number(a[1]) + Number(b[1])) / 2) * (Math.PI / 180);
  const metrosX = (Number(a[0]) - Number(b[0])) * 111320 * Math.cos(lat);
  const metrosY = (Number(a[1]) - Number(b[1])) * 110540;
  return Math.hypot(metrosX, metrosY);
}

function chaveDaCoord(coord) {
  return `${Number(coord[0]).toFixed(6)},${Number(coord[1]).toFixed(6)}`;
}

function heapPush(heap, item) {
  heap.push(item);
  let i = heap.length - 1;
  while (i > 0) {
    const pai = (i - 1) >> 1;
    if (heap[pai].dist <= heap[i].dist) break;
    const troca = heap[pai];
    heap[pai] = heap[i];
    heap[i] = troca;
    i = pai;
  }
}

function heapPop(heap) {
  const topo = heap[0];
  const ultimo = heap.pop();
  if (!heap.length || !ultimo) return topo;
  heap[0] = ultimo;
  let i = 0;
  for (;;) {
    const esquerda = (i * 2) + 1;
    const direita = esquerda + 1;
    let menor = i;
    if (esquerda < heap.length && heap[esquerda].dist < heap[menor].dist) menor = esquerda;
    if (direita < heap.length && heap[direita].dist < heap[menor].dist) menor = direita;
    if (menor === i) break;
    const troca = heap[menor];
    heap[menor] = heap[i];
    heap[i] = troca;
    i = menor;
  }
  return topo;
}

export function tracarEntre(collection, inicio, fim) {
  if (!inicio || !fim || !collection) return null;
  const adj = new Map();
  const coords = new Map();
  const liga = (a, b) => {
    const ka = chaveDaCoord(a);
    const kb = chaveDaCoord(b);
    if (!adj.has(ka)) adj.set(ka, []);
    if (!adj.has(kb)) adj.set(kb, []);
    if (!coords.has(ka)) coords.set(ka, a);
    if (!coords.has(kb)) coords.set(kb, b);
    if (ka === kb) return;
    const metros = metrosEntre(a, b);
    adj.get(ka).push({ key: kb, metros });
    adj.get(kb).push({ key: ka, metros });
  };
  ((collection && collection.features) || []).forEach((feature) => {
    const linha = coordenadasDaLinha(feature.geometry);
    for (let i = 1; i < linha.length; i += 1) liga(linha[i - 1], linha[i]);
  });
  if (!coords.size) return null;
  const ancorar = (coord) => {
    const key = chaveDaCoord(coord);
    if (!adj.has(key)) adj.set(key, []);
    if (!coords.has(key)) coords.set(key, coord);
    coords.forEach((outro, outroKey) => {
      if (outroKey === key) return;
      const metros = metrosEntre(coord, outro);
      if (metros <= 35) {
        adj.get(key).push({ key: outroKey, metros });
        adj.get(outroKey).push({ key, metros });
      }
    });
    return key;
  };
  const saida = ancorar(inicio);
  const chegada = ancorar(fim);
  const dist = new Map();
  const prev = new Map();
  const heap = [];
  dist.set(saida, 0);
  heapPush(heap, { dist: 0, key: saida });
  let alvo = null;
  while (heap.length) {
    const atual = heapPop(heap);
    if (!atual || atual.dist !== dist.get(atual.key)) continue;
    if (atual.key === chegada) {
      alvo = atual.key;
      break;
    }
    (adj.get(atual.key) || []).forEach((aresta) => {
      const proximo = atual.dist + aresta.metros;
      if (proximo < (dist.has(aresta.key) ? dist.get(aresta.key) : Infinity)) {
        dist.set(aresta.key, proximo);
        prev.set(aresta.key, atual.key);
        heapPush(heap, { dist: proximo, key: aresta.key });
      }
    });
  }
  if (!alvo) return null;
  const chaves = [alvo];
  const visitadas = new Set([alvo]);
  while (prev.has(chaves[0]) && !visitadas.has(prev.get(chaves[0]))) {
    const anterior = prev.get(chaves[0]);
    visitadas.add(anterior);
    chaves.unshift(anterior);
  }
  const linha = chaves.map((key) => coords.get(key)).filter(Boolean);
  return linha.length > 1 ? linha : null;
}

export function estaNoTrajeto(feature, caminho) {
  if (!feature || !feature.geometry || !caminho) return false;
  const segmentos = segmentosDoCaminho(caminho);
  if (!segmentos.length) return false;
  const coords = feature.geometry.type === 'Point'
    ? [feature.geometry.coordinates]
    : coordenadasDaLinha(feature.geometry);
  if (!coords.length) return false;
  return coords.every((coord) => (
    segmentos.some((segmento) => metrosAteSegmento(coord, segmento[0], segmento[1]) <= METROS_NO_TRECHO)
  ));
}

const TIPOS_LINHA_MARCA = new Set(['PROTECAO', 'EQUIPAMENTO', 'NA', 'REGULADOR']);
const METROS_MARCA_LINHA = 12;
const METROS_MARCA_SIMBOLO = 150;

function distanciaAoCaminho(coord, segmentos) {
  let menor = Infinity;
  segmentos.forEach((segmento) => {
    const distancia = metrosAteSegmento(coord, segmento[0], segmento[1]);
    if (distancia < menor) menor = distancia;
  });
  return menor;
}

export function filtrarForaDoTrecho(rede, caminho) {
  const segmentos = segmentosDoCaminho(caminho);
  if (!segmentos.length) return rede || EMPTY_COLLECTION;
  const features = ((rede && rede.features) || []).filter((feature) => {
    const geometry = feature.geometry;
    if (!geometry || geometry.type !== 'Point' || !Array.isArray(geometry.coordinates)) return false;
    return distanciaAoCaminho(geometry.coordinates, segmentos) <= METROS_NO_TRECHO;
  });
  return { type: 'FeatureCollection', features };
}

export function marcasDoTrecho(rede, caminho) {
  const segmentos = segmentosDoCaminho(caminho);
  if (!segmentos.length) return EMPTY_COLLECTION;
  const features = ((rede && rede.features) || []).filter((feature) => {
    const geometry = feature && feature.geometry;
    const tipo = String((feature && feature.properties && feature.properties.tipo) || '');
    if (!geometry) return false;
    if (geometry.type === 'Point') {
      if (!tipo.startsWith('TRAFO')) return false;
      return distanciaAoCaminho(geometry.coordinates, segmentos) <= METROS_MARCA_SIMBOLO;
    }
    if (geometry.type !== 'LineString' && geometry.type !== 'MultiLineString') return false;
    const coords = coordenadasDaLinha(geometry);
    if (!coords.length) return false;
    const menor = coords.reduce((atual, coord) => Math.min(atual, distanciaAoCaminho(coord, segmentos)), Infinity);
    if (TIPOS_LINHA_MARCA.has(tipo)) return menor <= METROS_MARCA_LINHA;
    if (tipo === 'TRECHO_BT') return menor <= METROS_MARCA_SIMBOLO;
    return false;
  });
  return { type: 'FeatureCollection', features };
}

function ehLinha(feature) {
  const type = feature && feature.geometry && feature.geometry.type;
  return type === 'LineString' || type === 'MultiLineString';
}

function coordenadasDoFeature(feature) {
  const geometry = feature && feature.geometry;
  if (!geometry) return [];
  if (geometry.type === 'Point' && Array.isArray(geometry.coordinates)) return [geometry.coordinates];
  return coordenadasDaLinha(geometry);
}

function idDaLinha(feature) {
  const properties = (feature && feature.properties) || {};
  const coords = coordenadasDoFeature(feature);
  const assinatura = coords
    .map((coord) => `${Number(coord[0]).toFixed(7)},${Number(coord[1]).toFixed(7)}`)
    .join(';');
  return `${properties.chave || ''}|${properties.tipo || ''}|${assinatura}`;
}

export function linhasDoAjuste(item) {
  const linhas = [];
  if (item && item.coordenadas && item.coordenadas.length >= 2) linhas.push(item.coordenadas);
  ((item && item.ramais) || []).forEach((ramal) => {
    if (ramal && ramal.coordenadas && ramal.coordenadas.length >= 2) linhas.push(ramal.coordenadas);
  });
  return linhas;
}

function colecaoDasLinhas(linhas) {
  return {
    type: 'FeatureCollection',
    features: linhas.map((coordinates) => ({
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates },
    })),
  };
}

export function pontoNoTrajeto(lngLat, caminho) {
  if (!lngLat || lngLat.length < 2) return false;
  return estaNoTrajeto({
    properties: { nome: 'ponto' },
    geometry: { type: 'Point', coordinates: lngLat },
  }, caminho);
}

export function trajetoEfetivo(caminho, trechos, modo) {
  const features = ((caminho && caminho.features) || []).slice();
  if (modo === 'adicionar') {
    (trechos || []).forEach((item) => {
      if (!item || item.tipo !== 'adicionar') return;
      linhasDoAjuste(item).forEach((coordinates) => {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates },
        });
      });
    });
  }
  return { type: 'FeatureCollection', features };
}

export function adicaoParaContinuar(trechos, lngLat, caminho) {
  if (!lngLat || pontoNoTrajeto(lngLat, caminho)) return null;
  return (trechos || []).find((item) => (
    item
    && item.tipo === 'adicionar'
    && pontoNoTrajeto(lngLat, colecaoDasLinhas(linhasDoAjuste(item)))
  )) || null;
}

function pontaDaLinha(coordenadas, lngLat) {
  if (!coordenadas || coordenadas.length < 2 || !lngLat) return null;
  if (metrosEntre(coordenadas[0], lngLat) <= 12) return 'inicio';
  if (metrosEntre(coordenadas[coordenadas.length - 1], lngLat) <= 12) return 'fim';
  return null;
}

export function continuarAdicao(item, origem, destino, coordenadas) {
  const ponta = pontaDaLinha(item.coordenadas, origem.lngLat);
  if (ponta === 'fim') {
    return {
      ...item,
      ate: destino,
      coordenadas: item.coordenadas.concat(coordenadas.slice(1)),
    };
  }
  if (ponta === 'inicio') {
    const invertida = coordenadas.slice().reverse();
    return {
      ...item,
      de: destino,
      coordenadas: invertida.concat(item.coordenadas.slice(1)),
    };
  }
  return {
    ...item,
    ramais: (item.ramais || []).concat([{ de: origem, ate: destino, coordenadas }]),
  };
}

function featureParaApi(feature) {
  const properties = (feature && feature.properties) || {};
  const item = {};
  if (properties.chave) item.chave = properties.chave;
  if (properties.nome) item.nome = properties.nome;
  if (properties.tipo) item.tipo = properties.tipo;
  const coordenadas = coordenadasDoFeature(feature)
    .filter((coord) => Array.isArray(coord) && coord.length >= 2)
    .map((coord) => coord.slice());
  if (coordenadas.length) item.coordenadas = coordenadas;
  return item;
}

function ehPonto(feature) {
  return Boolean(feature && feature.geometry && feature.geometry.type === 'Point');
}

export function trechoParaCalculo(caminho, rede, ajustes) {
  const base = ((caminho && caminho.features) || []).filter(ehLinha);
  const fora = new Set();
  const extras = [];
  const vistos = new Set(base.map(idDaLinha));
  (ajustes || []).forEach((item) => {
    if (!item || !item.coordenadas || item.coordenadas.length < 2) return;
    const pedaco = colecaoDasLinhas(linhasDoAjuste(item));
    if (item.tipo === 'remover') {
      base.forEach((feature) => {
        if (estaNoTrajeto(feature, pedaco)) fora.add(idDaLinha(feature));
      });
      return;
    }
    if (item.tipo !== 'adicionar') return;
    ((rede && rede.features) || []).filter(ehLinha).forEach((feature) => {
      const id = idDaLinha(feature);
      if (vistos.has(id) || !estaNoTrajeto(feature, pedaco)) return;
      vistos.add(id);
      extras.push(feature);
    });
  });
  const linhas = base.filter((feature) => !fora.has(idDaLinha(feature))).concat(extras);
  const trajeto = { type: 'FeatureCollection', features: linhas };
  const pontosVistos = new Set();
  const pontos = ((rede && rede.features) || []).filter((feature) => {
    if (!ehPonto(feature) || !estaNoTrajeto(feature, trajeto)) return false;
    const id = idDaLinha(feature);
    if (pontosVistos.has(id)) return false;
    pontosVistos.add(id);
    return true;
  });
  return linhas.concat(pontos).map(featureParaApi);
}

function distanciaQuadrada(a, b) {
  if (!a || !b || a.length < 2 || b.length < 2) return Infinity;
  const dx = Number(a[0]) - Number(b[0]);
  const dy = Number(a[1]) - Number(b[1]);
  return (dx * dx) + (dy * dy);
}

function tokenDoNome(nome) {
  const partes = String(nome || '').trim().split(/\s+/);
  return partes[partes.length - 1] || '';
}

export function linhaDoRotulo(collections, feature) {
  const properties = (feature && feature.properties) || {};
  if (properties.tipo !== 'ROTULO') return null;
  const ponto = feature.geometry && feature.geometry.type === 'Point' ? feature.geometry.coordinates : null;
  const token = tokenDoNome(properties.nome);
  if (!ponto || !token) return null;
  const candidatas = [];
  (collections || []).forEach((collection) => {
    ((collection && collection.features) || []).forEach((item) => {
      const geometry = item && item.geometry;
      if (!geometry || (geometry.type !== 'LineString' && geometry.type !== 'MultiLineString')) return;
      const nome = String((item.properties || {}).nome || '').trim();
      if (nome !== token) return;
      const dist = coordenadasDaLinha(geometry).reduce(
        (menor, coord) => Math.min(menor, distanciaQuadrada(coord, ponto)),
        Infinity,
      );
      candidatas.push({ feature: item, dist });
    });
  });
  if (!candidatas.length) return null;
  candidatas.sort((a, b) => a.dist - b.dist);
  const tocando = candidatas.filter((item) => item.dist <= 1e-10);
  if (tocando.length) return tocando[0].feature;
  if (candidatas.length === 1) return candidatas[0].feature;
  return null;
}

export function rotuloDeMapa(rede, end, lngLat) {
  const nome = end && end.nome ? String(end.nome).trim() : '';
  if (!nome) return '';
  let melhor = '';
  let melhorDist = Infinity;
  ((rede && rede.features) || []).forEach((feature) => {
    const properties = feature.properties || {};
    if (properties.tipo !== 'ROTULO' || !feature.geometry || feature.geometry.type !== 'Point') return;
    if (tokenDoNome(properties.nome) !== nome) return;
    const dist = lngLat ? distanciaQuadrada(feature.geometry.coordinates, lngLat) : 0;
    if (dist < melhorDist) {
      melhorDist = dist;
      melhor = String(properties.nome || '').trim();
    }
  });
  return melhor || nome;
}

export function lngLatDoPonto(point, feature) {
  const lat = Number(point && point.lat);
  const lon = Number(point && point.lon);
  if (Number.isFinite(lat) && Number.isFinite(lon)) return [lon, lat];
  const geometry = feature && feature.geometry;
  if (!geometry) return null;
  if (geometry.type === 'Point' && Array.isArray(geometry.coordinates)) return geometry.coordinates;
  if (geometry.type === 'LineString' && geometry.coordinates && geometry.coordinates[0]) {
    return geometry.coordinates[0];
  }
  return null;
}

export function fichaDoFeature(feature) {
  const properties = (feature && feature.properties) || {};
  const eletrico = asObject(properties.eletrico);
  const atributos = asObject(properties.atributos);
  const extras = Object.keys(atributos)
    .filter((key) => !ATRIBUTOS_JA_NA_FICHA.has(key) && atributos[key] != null && String(atributos[key]).trim() !== '')
    .map((key) => ({ label: key, value: String(atributos[key]) }));
  return {
    fase: eletrico.fase || atributos.Fase || null,
    tensao: asNumber(eletrico.tensaoNominalVolts),
    corrente: asNumber(eletrico.correnteNominalAmperes),
    estado: eletrico.estado || atributos.Estado || null,
    posicaoOperada: atributos['Posição operada'] || null,
    posicaoPlanejada: atributos['Posição planejada'] || null,
    kvaTotal: asNumber(eletrico.kvaTotal),
    kvaDistribuido: asNumber(eletrico.kvaDistribuido),
    kvaPrimario: asNumber(eletrico.kvaPrimario),
    kvaRural: asNumber(eletrico.kvaRural),
    clientes: asNumber(eletrico.clientesTotais),
    clientesDistribuidos: asNumber(eletrico.clientesDistribuidos),
    clientesPrimarios: asNumber(eletrico.clientesPrimarios),
    clientesRurais: asNumber(eletrico.clientesRurais),
    estiloOrigem: properties.estiloOrigem || null,
    alimentador: properties.alimentador || null,
    categoria: properties.categoria || null,
    cor: properties.corResolvida || null,
    extras,
  };
}

export function resumoDoPonto(ficha) {
  if (!ficha) return '';
  const parts = [];
  if (ficha.fase) parts.push(`Fase ${ficha.fase}`);
  if (ficha.tensao != null) parts.push(`${ficha.tensao.toLocaleString('pt-BR')} V`);
  if (ficha.kvaTotal != null) parts.push(`${ficha.kvaTotal.toLocaleString('pt-BR')} kVA`);
  if (ficha.clientes != null) parts.push(`${ficha.clientes.toLocaleString('pt-BR')} clientes`);
  return parts.join(' · ');
}

const TIPO_LABEL = {
  TRAFO_MONOFASICO: 'Transformador monofásico',
  TRAFO_TRIFASICO: 'Transformador trifásico',
  POSTE_CONCRETO: 'Poste de concreto',
  POSTE_MADEIRA: 'Poste de madeira',
  POSTE: 'Poste',
  ROTULO: 'Rótulo',
  REGULADOR: 'Regulador',
  SUBESTACAO: 'Subestação',
  PROTECAO: 'Proteção',
  NA: 'Chave NA',
  TRECHO_TRONCO: 'Trecho tronco',
  TRECHO_TRIFASICO: 'Trecho trifásico',
  TRECHO_BT: 'Trecho de baixa tensão',
};

const CATEGORIA_LABEL = {
  Religador: 'Religador',
  'Chave Fusível': 'Chave fusível',
  'CH. Fus. Repet.': 'Chave fusível repetidora',
  'Faca Unipolar': 'Faca unipolar',
  'CH Faca Adapt': 'Chave faca adaptada',
  Regulador: 'Regulador',
};

const CRITICIDADE_LABEL = {
  BAIXA: 'Baixa',
  MEDIA: 'Média',
  MÉDIA: 'Média',
  ALTA: 'Alta',
};

const ELETRICO_CAMPOS = [
  ['fase', 'Fase'],
  ['estado', 'Estado'],
  ['posicaoOperada', 'Posição operada'],
  ['posicaoPlanejada', 'Posição planejada'],
  ['tensaoNominalKv', 'Tensão nominal', 'kV'],
  ['tensaoNominalVolts', 'Tensão nominal', 'V'],
  ['tensaoPrimariaKv', 'Tensão primária', 'kV'],
  ['tensaoSecundariaKv', 'Tensão secundária', 'kV'],
  ['correnteNominalAmperes', 'Corrente nominal', 'A'],
  ['correntePontaFaseAAmperes', 'Corrente de ponta fase A', 'A'],
  ['correntePontaFaseBAmperes', 'Corrente de ponta fase B', 'A'],
  ['correntePontaFaseCAmperes', 'Corrente de ponta fase C', 'A'],
  ['correnteForaPontaFaseAAmperes', 'Corrente fora de ponta fase A', 'A'],
  ['correnteForaPontaFaseBAmperes', 'Corrente fora de ponta fase B', 'A'],
  ['correnteForaPontaFaseCAmperes', 'Corrente fora de ponta fase C', 'A'],
  ['potenciaNominalKva', 'Potência nominal', 'kVA'],
  ['carregamentoPercentual', 'Carregamento percentual', '%'],
  ['kvaDistribuido', 'kVA distribuído'],
  ['kvaPrimario', 'kVA primário'],
  ['kvaRural', 'kVA rural'],
  ['kvaTotal', 'kVA total'],
  ['clientesDistribuidos', 'Clientes distribuídos'],
  ['clientesPrimarios', 'Clientes primários'],
  ['clientesRurais', 'Clientes rurais'],
  ['clientesTotais', 'Clientes totais'],
  ['area', 'Área'],
  ['proprietario', 'Proprietário'],
  ['condFase', 'Condutor da fase'],
  ['condNeutro', 'Condutor do neutro'],
  ['gdNumeroInstalacao', 'GD instalação'],
  ['gdPotenciaInjetadaKw', 'GD potência injetada', 'kW'],
  ['gdNumeroSerie', 'GD número de série'],
];

const ATRIBUTO_NO_ELETRICO = {
  Fase: ['fase'],
  Estado: ['estado'],
  'Posição operada': ['posicaoOperada'],
  'Posição planejada': ['posicaoPlanejada'],
  'Tensão nominal': ['tensaoNominalKv', 'tensaoNominalVolts'],
  'Corrente nominal': ['correnteNominalAmperes'],
  Carregamento: ['carregamentoPercentual'],
  'kVA Distribuído': ['kvaDistribuido'],
  'kVA Primário': ['kvaPrimario'],
  'kVA Rural': ['kvaRural'],
  'kVA total': ['kvaTotal'],
  'Clientes Distribuídos': ['clientesDistribuidos'],
  'Clientes Primários': ['clientesPrimarios'],
  'Clientes Rurais': ['clientesRurais'],
  'Clientes Totais': ['clientesTotais'],
};

function asList(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }
  return [];
}

function textoPreenchido(value) {
  return value != null && String(value).trim() !== '';
}

function formatMedida(value, suffix) {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  const text = number.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  if (!suffix) return text;
  if (suffix === '%') return `${text}%`;
  return `${text} ${suffix}`;
}

export function rotuloDoTipo(member) {
  if (!member) return '';
  const categoria = member.categoria && String(member.categoria).trim();
  if (categoria) return CATEGORIA_LABEL[categoria] || categoria;
  const tipo = member.tipo;
  if (TIPO_LABEL[tipo]) return TIPO_LABEL[tipo];
  return '';
}

export function linhasDoMembro(member) {
  if (!member) return [];
  const eletrico = asObject(member.eletrico);
  const linhas = [];
  const push = (label, value) => {
    if (!textoPreenchido(value)) return;
    linhas.push({ label, value: String(value) });
  };
  ELETRICO_CAMPOS.forEach(([key, label, suffix]) => {
    if (!textoPreenchido(eletrico[key])) return;
    push(label, suffix ? formatMedida(eletrico[key], suffix) : eletrico[key]);
  });
  push('Alimentador', member.alimentador);
  if (textoPreenchido(member.criticidade)) {
    push('Criticidade', CRITICIDADE_LABEL[member.criticidade] || member.criticidade);
  }
  push('Coordenada UTM', member.coordenadaUtm);
  push('Estilo', member.estiloOrigem);
  if (textoPreenchido(member.nomeReal) && member.nomeReal !== member.nome) {
    push('Nome', member.nomeReal);
  }
  const atributos = asObject(member.atributos);
  const utm = member.coordenadaUtm ? String(member.coordenadaUtm) : '';
  Object.keys(atributos).forEach((key) => {
    const value = atributos[key];
    if (!textoPreenchido(value)) return;
    const campos = ATRIBUTO_NO_ELETRICO[key];
    if (campos && campos.some((campo) => textoPreenchido(eletrico[campo]))) return;
    if (utm && utm.includes(String(key)) && utm.includes(String(value))) return;
    push(key, value);
  });
  return linhas;
}

const RESUMO_CURTO = [
  ['Fase', (value) => `Fase ${value}`],
  ['kVA total', (value) => `${value} kVA`],
  ['Clientes totais', (value) => `${value} clientes`],
  ['Carregamento percentual', (value) => value],
  ['Tensão nominal', (value) => value],
  ['Coordenada UTM', (value) => `UTM ${value}`],
];

export function resumoCurtoDoMembro(member) {
  const porLabel = new Map(linhasDoMembro(member).map((linha) => [linha.label, linha.value]));
  const partes = [];
  RESUMO_CURTO.forEach(([label, texto]) => {
    if (partes.length >= 3) return;
    const value = porLabel.get(label);
    if (value) partes.push(texto(value));
  });
  return partes.join(' · ');
}

function chaveDoMembro(member) {
  return [
    member.tipo || '',
    member.nome || '',
    member.coordenadaUtm || '',
    JSON.stringify(member.atributos || {}),
    JSON.stringify(member.eletrico || {}),
  ].join('|');
}

export function membrosVisiveis(feature) {
  const properties = (feature && feature.properties) || {};
  const recebidos = asList(properties.membros);
  let lista = recebidos.length
    ? recebidos.map((member) => ({
      ...member,
      principal: member.chave === properties.chave && member.tipo === properties.tipo,
    }))
    : [{ ...properties, principal: true }];
  if (!lista.some((member) => member.principal)) {
    lista = lista.map((member, index) => ({ ...member, principal: index === 0 }));
  }
  lista.sort((a, b) => Number(Boolean(b.principal)) - Number(Boolean(a.principal)));
  const principal = lista.find((member) => member.principal) || properties;
  lista = lista.filter((member) => member.tipo !== 'ROTULO' || (member.nome && member.nome !== principal.nome));
  const vistos = new Set();
  return lista.filter((member) => {
    const chave = chaveDoMembro(member);
    if (vistos.has(chave)) return false;
    vistos.add(chave);
    return true;
  });
}

export function sugestaoFromResponse(data) {
  if (!data) return null;
  if (data.caminho || data.pontosFinais) return data;
  return data.sugestao || null;
}

export function kmPatchFromGeracao(current, pending) {
  if (!pending) return {};
  const next = {};
  const fill = (field, value) => {
    if (value == null || value === '') return;
    if (pending.mode === 'calc' || !hasKmValue(current[field])) next[field] = value;
  };
  fill('kmRdu', pending.kmUrbano);
  fill('kmRdr', pending.kmRural);
  fill('kmInspecao', pending.kmInspecao);
  return next;
}

export function collectBounds(collection, bounds) {
  const walk = (coords) => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === 'number') {
      bounds.extend(coords);
      return;
    }
    coords.forEach(walk);
  };
  (collection.features || []).forEach((feature) => {
    if (feature.geometry && feature.geometry.coordinates) walk(feature.geometry.coordinates);
  });
}
